import React from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Divider from '@mui/material/Divider';

const MATCH_TYPE_LABEL = {
  value: null, // shows the juror's actual value instead
  noAnswer: 'No answer',
  unmatched: 'No rule matched',
};

/**
 * Full "scoring equation" breakdown for one computed score — every
 * contributing category, the juror's value (or "No answer"/"No rule
 * matched"), and its delta — plus the running total and the "X of Y
 * categories answered" completeness indicator. Opened by clicking a score
 * badge on a juror card / juror dialog; never a black box.
 */
const ScoreDetailPopover = ({ open, anchorEl, onClose, score }) => {
  if (!score) {
    return null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Box sx={{ p: 2, minWidth: 320, maxWidth: 420 }}>
        <Typography variant="subtitle1">{score.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {score.answeredCount} of {score.ruleCategoryCount} {score.ruleCategoryCount === 1 ? 'category' : 'categories'} answered
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Value</TableCell>
              <TableCell align="right">Delta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {score.contributions.map((contribution) => (
              <TableRow key={contribution.categoryName}>
                <TableCell>{contribution.categoryName}</TableCell>
                <TableCell>
                  {contribution.matchType === 'value'
                    ? contribution.value
                    : MATCH_TYPE_LABEL[contribution.matchType]}
                </TableCell>
                <TableCell align="right">
                  {contribution.delta > 0 ? `+${contribution.delta}` : contribution.delta}
                </TableCell>
              </TableRow>
            ))}
            {score.contributions.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No scoring rules configured for this score.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2">Total</Typography>
          <Typography variant="subtitle2">
            {score.total > 0 ? `+${score.total}` : score.total}
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};

export default ScoreDetailPopover;
